import { AlertTriangle, OctagonAlert } from "lucide-react";

import type { DiagnosticCounts, DiagnosticSummary } from "../../../../workflows-api";
import { formatTimestamp } from "./run-detail-format";

/**
 * Captured warning/error surfaces for a run — ported from nazare-frontend's
 * `screens/admin/workflows/diagnostics.tsx`. A diagnostic is a named
 * `{severity, code, message, stepId}` the run emitted, distinct from a hard
 * run failure. `DiagnosticChips` is the compact rollup shown in the run
 * header; `DiagnosticRow` is a single entry on the Diagnostics feed, both as
 * its own spine node (run-level) and nested inside the step accordion that
 * emitted it.
 */

/** Compact warn/error rollup chips. Renders nothing when both counts are zero. */
export function DiagnosticChips({ counts }: { counts: DiagnosticCounts | null | undefined }) {
  if (!counts) {
    return <span className="text-[11px] text-[var(--nzr-muted)]">Diagnostics calculating…</span>;
  }
  if (counts.warn === 0 && counts.error === 0) return null;
  return (
    <span className="inline-flex items-center gap-1.5">
      {counts.error > 0 && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium bg-[var(--red-bg)] text-[var(--red)] border border-[var(--red)] text-xs tabular-nums"
          title={`${counts.error.toLocaleString("en-GB")} error diagnostic${counts.error === 1 ? "" : "s"}`}
        >
          <OctagonAlert size={13} />
          {counts.error.toLocaleString("en-GB")}
        </span>
      )}
      {counts.warn > 0 && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium bg-[var(--orange-bg)] text-[var(--orange)] border border-[var(--orange)] text-xs tabular-nums"
          title={`${counts.warn.toLocaleString("en-GB")} warning diagnostic${counts.warn === 1 ? "" : "s"}`}
        >
          <AlertTriangle size={13} />
          {counts.warn.toLocaleString("en-GB")}
        </span>
      )}
    </span>
  );
}

export function SeverityChip({ severity }: { severity: DiagnosticSummary["severity"] }) {
  const isError = severity === "error";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${
        isError ? "bg-[var(--red-bg)] text-[var(--red)] border-[var(--red)]" : "bg-[var(--orange-bg)] text-[var(--orange)] border-[var(--orange)]"
      }`}
    >
      {isError ? <OctagonAlert size={11} /> : <AlertTriangle size={11} />}
      {isError ? "Error" : "Warning"}
    </span>
  );
}

/**
 * A single diagnostic entry. `hideStep` suppresses the emitting step's id —
 * used when the row is already nested inside that step's accordion on the
 * Diagnostics feed, since the step is then implicit.
 */
export function DiagnosticRow({
  diagnostic,
  hideStep = false,
}: {
  diagnostic: DiagnosticSummary;
  hideStep?: boolean;
}) {
  return (
    <div className="flex items-start gap-3 py-2 px-3 border-t border-[var(--border-soft)] first:border-t-0">
      <span className="mt-0.5 shrink-0">
        <SeverityChip severity={diagnostic.severity} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[12px] font-mono text-[var(--ink)]">{diagnostic.code}</span>
          {!hideStep && diagnostic.stepId && (
            <span className="text-[11px] text-[var(--nzr-muted)] font-mono truncate">{diagnostic.stepId}</span>
          )}
          <span className="text-[11px] text-[var(--nzr-muted)]">{formatTimestamp(diagnostic.createdAt)}</span>
        </div>
        <div className="text-[13px] text-[var(--ink)] whitespace-pre-wrap break-words mt-0.5">
          {diagnostic.message}
        </div>
      </div>
    </div>
  );
}
